// API + utils para el módulo de entregas
import { supabase } from './supabase'

export const CUTOFF_HOUR = 16
export const DIAS = ['domingo','lunes','martes','miercoles','jueves','viernes','sabado']
export const DIA_LABEL = {
  lunes:'Lunes', martes:'Martes', miercoles:'Miércoles', jueves:'Jueves',
  viernes:'Viernes', sabado:'Sábado', domingo:'Domingo'
}

/** Antes del corte → reparte HOY. Después → MAÑANA. */
export function getDiaReparto(now = new Date()) {
  const hora = now.getHours()
  const dia = now.getDay()
  return hora < CUTOFF_HOUR ? dia : (dia + 1) % 7
}

export function getDiaRepartoLabel(now = new Date()) {
  return DIA_LABEL[DIAS[getDiaReparto(now)]]
}

export function isAfterCutoff(now = new Date()) {
  return now.getHours() >= CUTOFF_HOUR
}

/**
 * Fix #25 (7-jul-2026): ¿Ya pasó el corte D-1 (16hs del día anterior a la entrega)?
 * Antes del corte: el sistema NO debería permitir armar ni salir a ruta
 * (el cron D-1 aún no ejecutó el cobro, no sabemos si va a poder cobrarse).
 * Ejemplo: entrega jueves 9-jul → corte miércoles 8-jul 16hs.
 */
export function yaPasoCorteD1(fechaEntrega, now = new Date()) {
  if (!fechaEntrega) return false
  const corte = new Date(fechaEntrega + 'T16:00:00')
  corte.setDate(corte.getDate() - 1)
  return now >= corte
}

/** Fecha que se va a repartir (today o tomorrow). */
export function getFechaReparto(now = new Date()) {
  const d = new Date(now)
  if (isAfterCutoff(now)) d.setDate(d.getDate() + 1)
  return d.toISOString().slice(0, 10)
}

/**
 * Trae entregas para una fecha o rango.
 * getEntregas('2026-07-09') — 1 sola fecha
 * getEntregas({ desde: '2026-07-07', hasta: '2026-07-13' }) — rango cerrado inclusive
 * Nico feedback 7-jul-2026: necesitaba ver el pipeline sin ir día por día.
 */
export async function getEntregas(fechaOrRango) {
  const esRango = fechaOrRango && typeof fechaOrRango === 'object' && fechaOrRango.desde
  let query = supabase
    .from('entregas')
    .select(`
      id, numero_orden, tracking_token, fecha_programada, ventana_horaria, tipo,
      kit_codigo, productos_extras, estado, bultos_total, monto_total,
      repartidor_id, ts_en_ruta, ts_entregada, etiqueta_impresa_at,
      ts_no_entregada, motivo_no_entrega, foto_url, recibido_por, pago_id, es_prueba,
      km_recorridos, km_regreso_origen, km_origen, canal,
      cliente:clientes!entregas_cliente_id_fkey(
        id, barrio, direccion, apto, dia_entrega, alias,
        titular:profiles!clientes_titular_fkey(id, nombre, phone),
        beneficiario:profiles!clientes_beneficiario_fkey(id, nombre, phone)
      ),
      kit:kits!entregas_kit_codigo_fkey(nombre, tier),
      repartidor:profiles!entregas_repartidor_id_fkey(nombre, role)
    `)
  if (esRango) {
    query = query.gte('fecha_programada', fechaOrRango.desde).lte('fecha_programada', fechaOrRango.hasta)
  } else {
    query = query.eq('fecha_programada', fechaOrRango)
  }
  const { data, error } = await query
    .order('fecha_programada')
    .order('ventana_horaria')
    .order('barrio', { foreignTable: 'cliente', ascending: true })
  if (error) {
    console.error('getEntregas error:', error)
    return []
  }
  return await mergearDatosPedidoOneShot(data || [])
}

/** Para entregas one-shot (sin cliente_id) armar un `cliente` mock:
 *  1) usar snapshot campos de la entrega (facturacion_razon_social, direccion_snapshot, barrio_snapshot)
 *  2) linkear por numero_orden con el pedido origen (no por entrega_id — esa columna no existe)
 *  3) merge de datos: pedido gana sobre snapshot solo si el snapshot no tiene el dato
 *  Además añade `facturacion_razon_social`, `facturacion_tipo` y campos snapshot al top-level. */
export async function mergearDatosPedidoOneShot(entregas) {
  const sinCliente = entregas.filter(e => !e.cliente)
  if (sinCliente.length === 0) return entregas

  // Traer entregas completas con snapshot fields para las que no vinieron con esos campos
  const ids = sinCliente.map(e => e.id)
  const { data: snapshots } = await supabase
    .from('entregas')
    .select('id, numero_orden, facturacion_tipo, facturacion_razon_social, nombre_fantasia_snapshot, direccion_snapshot, apto_snapshot, barrio_snapshot, lat_snapshot, lng_snapshot')
    .in('id', ids)
  const snapById = Object.fromEntries((snapshots || []).map(s => [s.id, s]))

  // Linkear por numero_orden con el pedido origen
  const numeros = sinCliente.map(e => e.numero_orden).filter(Boolean)
  let pedidosByNumero = {}
  if (numeros.length > 0) {
    const { data: pedidos } = await supabase
      .from('pedidos')
      .select(`
        numero_orden, comprador_nombre, comprador_telefono, comprador_email,
        entrega_direccion, entrega_apto, entrega_lat, entrega_lng,
        monto_total, items, notas,
        barrio:barrios!pedidos_entrega_barrio_id_fkey(id, nombre)
      `)
      .in('numero_orden', numeros)
    pedidosByNumero = Object.fromEntries((pedidos || []).map(p => [p.numero_orden, p]))
  }

  return entregas.map(e => {
    if (e.cliente) return e
    const snap = snapById[e.id] || {}
    const pe = pedidosByNumero[e.numero_orden] || {}

    const razonSocial = snap.facturacion_razon_social || pe.comprador_nombre || 'Pedido único'
    const fantasia = snap.nombre_fantasia_snapshot
    const nombre = fantasia || razonSocial
    const telefono = pe.comprador_telefono || null
    const direccion = snap.direccion_snapshot || pe.entrega_direccion || ''
    const apto = snap.apto_snapshot || pe.entrega_apto || ''
    const barrio = snap.barrio_snapshot || pe.barrio?.nombre || ''
    const esB2B = snap.facturacion_tipo === 'empresa'

    return {
      ...e,
      facturacion_tipo: snap.facturacion_tipo,
      facturacion_razon_social: snap.facturacion_razon_social,
      nombre_fantasia_snapshot: fantasia,
      direccion_snapshot: direccion,
      apto_snapshot: apto,
      barrio_snapshot: barrio,
      lat_snapshot: snap.lat_snapshot || pe.entrega_lat,
      lng_snapshot: snap.lng_snapshot || pe.entrega_lng,
      // Items del pedido origen — desglose para chequear al armar bulto
      pedido_items: Array.isArray(pe.items) ? pe.items : [],
      cliente: {
        alias: esB2B ? '🏢 B2B' : '📦 Pedido único',
        barrio,
        direccion,
        apto,
        lat: snap.lat_snapshot || pe.entrega_lat,
        lng: snap.lng_snapshot || pe.entrega_lng,
        notas: pe.notas,
        titular: { nombre, phone: telefono },
        __esOneShot: true,
        __esB2B: esB2B,
        __razon_social: razonSocial,
        __fantasia: fantasia,
      },
    }
  })
}

/** Trae las entregas asignadas a un repartidor para una fecha. */
export async function getMisEntregas(repartidorId, fecha) {
  const { data, error } = await supabase
    .from('entregas')
    .select(`
      id, numero_orden, tracking_token, fecha_programada, ventana_horaria, tipo,
      kit_codigo, productos_extras, notas, estado, bultos_total,
      repartidor_id, ts_en_ruta, ts_entregada, etiqueta_impresa_at,
      ts_no_entregada, motivo_no_entrega, foto_url, recibido_por,
      cliente:clientes!entregas_cliente_id_fkey(
        id, barrio, direccion, apto, dia_entrega, tipo_vivienda, lat, lng, alias,
        titular:profiles!clientes_titular_fkey(id, nombre, phone),
        beneficiario:profiles!clientes_beneficiario_fkey(id, nombre, phone)
      ),
      kit:kits!entregas_kit_codigo_fkey(nombre, tier)
    `)
    .eq('fecha_programada', fecha)
    .eq('repartidor_id', repartidorId)
    .order('ventana_horaria')
    .order('barrio', { foreignTable: 'cliente', ascending: true })
  if (error) {
    console.error('getMisEntregas error:', error)
    return []
  }
  let entregas = await mergearDatosPedidoOneShot(data || [])
  entregas = await mergearItemsKit(entregas)
  return entregas
}

/** Resuelve los items del kit + productos_extras para cada entrega de suscripción.
 *  Set pedido_items en cada entrega con kit_codigo (los one-shot ya lo tienen de pedidos.items). */
async function mergearItemsKit(entregas) {
  const conKit = entregas.filter(e => e.kit_codigo && (!e.pedido_items || e.pedido_items.length === 0))
  if (conKit.length === 0) return entregas

  const codigos = [...new Set(conKit.map(e => e.kit_codigo))]
  // Via RPC SECURITY DEFINER: el repartidor NO lee la tabla productos (costo/margen oculto, #292).
  // Devuelve plano: { kit_codigo, cantidad, es_regalo, marca, nombre, presentacion }.
  const { data: kitItems, error } = await supabase
    .rpc('reparto_kit_items', { p_codigos: codigos })
  if (error) {
    console.error('mergearItemsKit error:', error)
    return entregas
  }

  // Agrupar items por kit_codigo → formato compatible con pedido_items (descripcion, cantidad)
  const porKit = {}
  for (const ki of (kitItems || [])) {
    const descripcion = [ki.marca, ki.nombre, ki.presentacion].filter(Boolean).join(' · ')
    if (!porKit[ki.kit_codigo]) porKit[ki.kit_codigo] = []
    porKit[ki.kit_codigo].push({
      descripcion: descripcion || 'Producto',
      cantidad: Number(ki.cantidad) || 1,
      es_regalo: !!ki.es_regalo,
      tipo: 'kit_item',
    })
  }

  return entregas.map(e => {
    if (!e.kit_codigo) return e
    if (e.pedido_items && e.pedido_items.length > 0) return e
    const items = [...(porKit[e.kit_codigo] || [])]
    // Agregar productos_extras si trae (jsonb array)
    if (Array.isArray(e.productos_extras)) {
      for (const ex of e.productos_extras) {
        items.push({
          descripcion: ex.descripcion || ex.nombre || 'Extra',
          cantidad: Number(ex.cantidad) || 1,
          tipo: 'extra',
        })
      }
    }
    return { ...e, pedido_items: items }
  })
}

/** Marca en ruta y dispara WA. */
export async function marcarEnRuta(entregaIds) {
  const { data, error } = await supabase.rpc('marcar_en_ruta', { entrega_ids: entregaIds })
  if (error) throw error
  // Dispara la edge function para procesar la cola
  await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/wa-send`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}` }
  }).catch(e => console.error('wa-send trigger:', e))
  return data
}

export async function confirmarEntrega({ token, nombre, doc, fotoPath }) {
  // foto_url en DB guarda el PATH del bucket privado (ej. "2026/05/abc.jpg"),
  // no la URL pública. Para mostrar usar getEntregaFotoSignedUrl(path).
  const { data, error } = await supabase.rpc('confirmar_entrega', {
    token_param: token,
    nombre_param: nombre,
    doc_param: doc,
    foto_url_param: fotoPath ?? null,
  })
  if (error) throw error
  // Dispara WA
  fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/wa-send`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}` }
  }).catch(e => console.error('wa-send trigger:', e))
  return data
}

/** Marca una entrega como no entregada (visita fallida).
 *  motivo: string (chip predefinido + texto libre opcional concatenado).
 *  fotoPath: path en bucket entregas-fotos (obligatorio, prueba de la visita). */
export async function marcarNoEntregada({ token, motivo, fotoPath }) {
  const { data, error } = await supabase.rpc('marcar_no_entregada', {
    token_param: token,
    motivo_param: motivo,
    foto_url_param: fotoPath ?? null,
  })
  if (error) throw error
  // Dispara WA
  fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/wa-send`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}` }
  }).catch(e => console.error('wa-send trigger:', e))
  return data
}

/** Sube una foto de entrega/visita al bucket privado y devuelve el PATH.
 *  kind: 'entrega' (entrega exitosa) o 'no-entrega' (visita fallida). */
export async function uploadEntregaFoto(token, file, kind = 'entrega') {
  if (!file) return null
  const yyyy = new Date().getFullYear()
  const mm = String(new Date().getMonth() + 1).padStart(2, '0')
  const prefix = kind === 'no-entrega' ? 'no-entregadas/' : ''
  const path = `${prefix}${yyyy}/${mm}/${token}.jpg`
  const { error } = await supabase.storage
    .from('entregas-fotos')
    .upload(path, file, { upsert: true, contentType: file.type })
  if (error) throw error
  return path
}

/** Genera signedUrl para una foto privada del bucket entregas-fotos.
 *  Default 7 dias (suficiente para la mayoria de reclamos). */
export async function getEntregaFotoSignedUrl(path, expiresIn = 7 * 24 * 3600) {
  if (!path) return null
  // Si por compatibilidad recibimos una URL completa vieja, la devolvemos como esta
  if (path.startsWith('http://') || path.startsWith('https://')) return path
  const { data, error } = await supabase.storage
    .from('entregas-fotos')
    .createSignedUrl(path, expiresIn)
  if (error) { console.error('signedUrl:', error); return null }
  return data?.signedUrl || null
}

/** Trae entregas con composición de kit + extras expandidos para la hoja de armado. */
export async function getEntregasArmado(entregaIds) {
  if (!entregaIds?.length) return []
  // 1. Entregas + cliente + kit
  const { data: entregas, error } = await supabase
    .from('entregas')
    .select(`
      id, numero_orden, fecha_programada, ventana_horaria, tipo,
      kit_codigo, productos_extras, notas, bultos_total,
      cliente:clientes!entregas_cliente_id_fkey(
        barrio, direccion, apto, tipo_vivienda, alias,
        titular:profiles!clientes_titular_fkey(nombre),
        beneficiario:profiles!clientes_beneficiario_fkey(nombre)
      ),
      kit:kits!entregas_kit_codigo_fkey(nombre, tier)
    `)
    .in('id', entregaIds)
    .order('ventana_horaria')
  if (error) { console.error(error); return [] }
  // Mergear datos de pedidos one-shot (entregas sin cliente suscriptor)
  // OJO: no mutar el array original, re-asignar la variable
  const entregasMerged = await mergearDatosPedidoOneShot(entregas || [])

  // 2. Para cada kit_codigo único, traer composición
  const kitCodigos = [...new Set(entregasMerged.map(e => e.kit_codigo).filter(Boolean))]
  let kitItems = []
  if (kitCodigos.length > 0) {
    const { data } = await supabase
      .from('kit_items')
      .select(`
        kit_codigo, cantidad, es_regalo,
        producto:productos(id, sku, nombre, marca, presentacion)
      `)
      .in('kit_codigo', kitCodigos)
    kitItems = data || []
  }

  // 3. Mapear items por kit
  const itemsByKit = {}
  kitItems.forEach(it => {
    if (!itemsByKit[it.kit_codigo]) itemsByKit[it.kit_codigo] = []
    itemsByKit[it.kit_codigo].push(it)
  })

  // 4. Resolver productos_extras → traer nombres si vienen con producto_id o sku
  const extraIds = new Set()
  const extraSkus = new Set()
  entregasMerged.forEach(e => {
    (e.productos_extras || []).forEach(x => {
      if (x?.producto_id) extraIds.add(x.producto_id)
      else if (x?.sku) extraSkus.add(x.sku)
    })
  })
  let extrasMap = {}
  if (extraIds.size > 0 || extraSkus.size > 0) {
    const filters = []
    if (extraIds.size > 0) filters.push(`id.in.(${[...extraIds].join(',')})`)
    if (extraSkus.size > 0) filters.push(`sku.in.(${[...extraSkus].join(',')})`)
    const { data } = await supabase
      .from('productos')
      .select('id, sku, nombre, marca, presentacion')
      .or(filters.join(','))
    ;(data || []).forEach(p => { extrasMap[p.id] = p; extrasMap[p.sku] = p })
  }

  // 5. Construir resultado normalizado
  return entregasMerged.map(e => {
    const kitItemsResolved = (itemsByKit[e.kit_codigo] || []).map(it => ({
      cantidad: Number(it.cantidad),
      sku: it.producto?.sku,
      nombre: it.producto?.nombre,
      marca: it.producto?.marca,
      presentacion: it.producto?.presentacion,
      es_regalo: !!it.es_regalo,
    }))
    const extrasResolved = (e.productos_extras || []).map(x => {
      const ref = x?.producto_id ? extrasMap[x.producto_id] : (x?.sku ? extrasMap[x.sku] : null)
      return {
        cantidad: Number(x?.cantidad ?? x?.qty ?? 1),
        sku: ref?.sku || x?.sku,
        nombre: ref?.nombre || x?.nombre || (typeof x === 'string' ? x : 'Producto extra'),
        marca: ref?.marca,
        presentacion: ref?.presentacion,
        es_regalo: !!x?.es_regalo,
      }
    })
    return { ...e, kit_items: kitItemsResolved, extras: extrasResolved, composicion: netearComposicion(kitItemsResolved, extrasResolved) }
  })
}

/** Netea kit_items + extras (los extras pueden traer cantidad negativa = quita)
 *  a la composición real de la caja. Colapsa por producto (id/sku), suma cantidades,
 *  descarta lo que quede en 0 o negativo. es_regalo se conserva si alguna fuente lo marca.
 *  Uso: hoja de armado / etiquetas muestran la caja real, sin líneas "−1". */
export function netearComposicion(kitItems = [], extras = []) {
  const m = {}
  const push = (it, esExtra) => {
    const key = it.producto_id || it.sku || it.nombre
    if (!key) return
    if (!m[key]) m[key] = { producto_id: it.producto_id, sku: it.sku, nombre: it.nombre, marca: it.marca, presentacion: it.presentacion, cantidad: 0, es_regalo: false, _origen: esExtra ? 'extra' : 'kit' }
    m[key].cantidad += Number(it.cantidad) || 0
    if (it.es_regalo) m[key].es_regalo = true
    if (esExtra && (Number(it.cantidad) || 0) > 0) m[key]._origen = 'extra'
  }
  ;(kitItems || []).forEach(it => push(it, false))
  ;(extras || []).forEach(it => push(it, true))
  return Object.values(m)
    .filter(x => (Number(x.cantidad) || 0) > 0)
    .sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''))
}

/** Lista todas las suscripciones (clientes) con titular, beneficiario, kit, portero, barrio. */
export async function getSuscripciones() {
  const { data, error } = await supabase
    .from('clientes')
    .select(`
      id, alias, barrio, direccion, apto, dia_entrega, ventana_horaria, semana_mes,
      tipo_vivienda, lat, lng, estado, pausa_hasta, dia_cobro_mes, modelo_cobro,
      kit_actual, fecha_inicio, fecha_proximo_cobro, notas, es_prueba,
      fuera_zona, entrega_responsable,
      barrio_obj:barrios!clientes_barrio_id_fkey(id, nombre, dias),
      titular:profiles!clientes_titular_fkey(id, nombre, phone, email, documento, documento_tipo, transferencia_habilitada),
      beneficiario:profiles!clientes_beneficiario_fkey(id, nombre, phone, email, documento, documento_tipo, transferencia_habilitada),
      kit:kits!clientes_kit_actual_fkey(codigo, nombre, tier, precio_susc),
      portero:porteros!clientes_portero_referido_id_fkey(id, lugar, profile:profiles!porteros_id_fkey(nombre))
    `)
    .order('created_at', { ascending: false })
  if (error) { console.error('getSuscripciones:', error); return [] }
  return data || []
}

/** Lista de usuarios disponibles para repartir (admin + repartidor activos). */
export async function getUsuariosReparto() {
  const { data, error } = await supabase.rpc('usuarios_reparto')
  if (error) { console.error('getUsuariosReparto:', error); return [] }
  return data || []
}

/** Asigna un repartidor a una o varias entregas. Pasar null para desasignar. */
export async function asignarRepartidor(entregaIds, repartidorId) {
  const ids = Array.isArray(entregaIds) ? entregaIds : [entregaIds]
  const { error } = await supabase
    .from('entregas')
    .update({ repartidor_id: repartidorId || null })
    .in('id', ids)
  if (error) throw error
}

/** Reagenda una entrega no_entregada a una nueva fecha y la deja como programada.
 *  Limpia los datos de la visita fallida pero conserva foto_url para historico. */
export async function reagendarEntrega(entregaId, fechaNueva) {
  const { error } = await supabase
    .from('entregas')
    .update({
      estado: 'programada',
      fecha_programada: fechaNueva,
      ts_no_entregada: null,
      motivo_no_entrega: null,
      ts_en_ruta: null,
    })
    .eq('id', entregaId)
  if (error) throw error
}

export async function actualizarBultos(entregaId, bultos) {
  const n = Math.max(1, Math.min(20, parseInt(bultos) || 1))
  const { error } = await supabase
    .from('entregas')
    .update({ bultos_total: n })
    .eq('id', entregaId)
  if (error) throw error
  return n
}

export async function getTrackingPublico(token) {
  const { data, error } = await supabase.rpc('tracking_publico', { token_param: token })
  if (error) throw error
  return data?.[0] || null
}

/** Devuelve el "destinatario físico" — beneficiario si existe, sino titular. */
export function quienRecibe(cliente) {
  if (!cliente) return null
  return cliente.beneficiario || cliente.titular || null
}

/** Devuelve el "titular" — quien paga, recibe WA del cobro. */
export function quienPaga(cliente) {
  return cliente?.titular || null
}

/** True si beneficiario != titular (la suscripción es para otra persona). */
export function esRegalo(cliente) {
  return !!(cliente?.beneficiario && cliente?.beneficiario?.id !== cliente?.titular?.id)
}

export const ESTADO_LABEL = {
  programada: 'Programada',
  en_ruta: 'En ruta',
  entregada: 'Entregada',
  no_entregada: 'No entregada',
  cancelada: 'Cancelada',
}

export const ESTADO_COLOR = {
  programada: { bg: '#E0F2FE', text: '#0C4A6E', dot: '#0EA5E9' },
  en_ruta: { bg: '#FEF3C7', text: '#92400E', dot: '#F59E0B' },
  entregada: { bg: '#D1FAE5', text: '#065F46', dot: '#10B981' },
  no_entregada: { bg: '#FEE2E2', text: '#991B1B', dot: '#EF4444' },
  cancelada: { bg: '#F1F5F9', text: '#475569', dot: '#64748B' },
}

// ============================================================================
// Edición de kit por entrega (sustituir / quitar / agregar / regalo)
// Modelo: la caja real = kit_items(template) + productos_extras(deltas, con
// cantidad negativa = quita). Los triggers de stock ya leen productos_extras,
// así que NO se toca ningún trigger. Auditoría en entrega_kit_cambios.
// ============================================================================

/** Items del kit template (para calcular el diff base al editar). */
export async function getKitTemplateItems(kitCodigo) {
  if (!kitCodigo) return []
  const { data, error } = await supabase
    .from('kit_items')
    .select('kit_codigo, cantidad, es_regalo, producto:productos(id, sku, nombre, marca, presentacion, precio_publico, iva_pct)')
    .eq('kit_codigo', kitCodigo)
  if (error) { console.error('getKitTemplateItems:', error); return [] }
  return (data || []).map(ki => ({
    producto_id: ki.producto?.id,
    sku: ki.producto?.sku,
    nombre: ki.producto?.nombre,
    marca: ki.producto?.marca,
    presentacion: ki.producto?.presentacion,
    precio_publico: Number(ki.producto?.precio_publico) || 0,
    iva_pct: ki.producto?.iva_pct,
    cantidad: Number(ki.cantidad) || 1,
    es_regalo: !!ki.es_regalo,
  }))
}

/** Catálogo de productos activos con stock, para el selector del editor. */
export async function getProductosCatalogo() {
  const { data, error } = await supabase
    .from('productos')
    .select('id, sku, nombre, marca, presentacion, precio_publico, iva_pct, stock_actual, stock_reservado, activo')
    .order('nombre')
  if (error) { console.error('getProductosCatalogo:', error); return [] }
  return (data || [])
    .filter(p => p.activo !== false)
    .map(p => ({
      ...p,
      precio_publico: Number(p.precio_publico) || 0,
      disponible: (Number(p.stock_actual) || 0) - (Number(p.stock_reservado) || 0),
    }))
}

/** Redondeo Limpack: hacia arriba, sin centavos. */
function ceilPrecio(n) { return Math.ceil(Number(n) || 0) }

/** Diff puro: template (kit_items) vs final (filas editadas) → productos_extras
 *  (deltas), filas de auditoría y monto_delta (solo lo marcado como cobrado).
 *  final[]: { producto_id, sku, nombre, cantidad, es_regalo, cobrado, precio_publico }
 *  Devuelve { productosExtras, cambios, montoDelta }. */
export function calcularExtrasDesdeEdicion(template = [], final = []) {
  const byId = {}
  const ensure = (id) => { if (!byId[id]) byId[id] = { t: null, f: null }; return byId[id] }
  ;(template || []).forEach(t => { if (t.producto_id) ensure(t.producto_id).t = t })
  ;(final || []).forEach(f => { if (f.producto_id) ensure(f.producto_id).f = f })

  const productosExtras = []
  const cambios = []
  let montoDelta = 0

  for (const id of Object.keys(byId)) {
    const { t, f } = byId[id]
    const tQty = t ? (Number(t.cantidad) || 0) : 0
    const fQty = f ? (Number(f.cantidad) || 0) : 0
    const delta = fQty - tQty
    const ref = f || t
    if (delta === 0) continue

    const esRegalo = !!(f && f.es_regalo)
    const cobrado = !!(f && f.cobrado)
    const precio = Number((f && f.precio_publico) ?? (ref && ref.precio_publico)) || 0

    const linea = {
      producto_id: id,
      sku: ref?.sku || null,
      nombre: ref?.nombre || null,
      cantidad: delta,
    }
    if (delta > 0 && esRegalo) { linea.es_regalo = true; linea.precio = 0; linea.origen = 'regalo' }
    else if (delta > 0) linea.origen = t ? 'mas_cantidad' : 'agregado'
    else linea.origen = 'quita'
    productosExtras.push(linea)

    if (delta < 0) {
      cambios.push({ accion: 'quita', producto_id: null, producto_original_id: id, producto_sku: null, producto_original_sku: ref?.sku || null, cantidad: -delta, es_regalo: false, cobrado: false, monto_delta: 0 })
    } else if (esRegalo) {
      cambios.push({ accion: 'regalo', producto_id: id, producto_original_id: null, producto_sku: ref?.sku || null, producto_original_sku: null, cantidad: delta, es_regalo: true, cobrado: false, monto_delta: 0 })
    } else {
      const md = cobrado ? ceilPrecio(precio * delta) : 0
      montoDelta += md
      cambios.push({ accion: 'agregar', producto_id: id, producto_original_id: null, producto_sku: ref?.sku || null, producto_original_sku: null, cantidad: delta, es_regalo: false, cobrado, monto_delta: md })
    }
  }
  return { productosExtras, cambios, montoDelta }
}

/** Persiste la edición del kit de una entrega + auditoría.
 *  productosExtras: array final (reemplaza el jsonb de la entrega).
 *  montoTotalNuevo: si viene, actualiza monto_total.
 *  cambios: filas de auditoría (sin entrega_id ni created_by, se completan acá). */
export async function guardarEdicionKit({ entregaId, productosExtras, montoTotalNuevo, cambios, motivo }) {
  const patch = { productos_extras: productosExtras || [] }
  if (montoTotalNuevo != null) patch.monto_total = montoTotalNuevo
  const { error: errUpd } = await supabase.from('entregas').update(patch).eq('id', entregaId)
  if (errUpd) throw errUpd

  let createdBy = null
  try { const { data } = await supabase.auth.getUser(); createdBy = data?.user?.id || null } catch {}

  if (Array.isArray(cambios) && cambios.length > 0) {
    const filas = cambios.map(c => ({ ...c, entrega_id: entregaId, motivo: motivo || null, created_by: createdBy }))
    const { error: errAud } = await supabase.from('entrega_kit_cambios').insert(filas)
    if (errAud) console.error('guardarEdicionKit auditoría:', errAud) // no bloquea la operación
  }
  return true
}

/** Historial de ediciones de kit de una entrega (para mostrar en el editor). */
export async function getHistorialKitEntrega(entregaId) {
  const { data, error } = await supabase
    .from('entrega_kit_cambios')
    .select('accion, producto_sku, producto_original_sku, cantidad, es_regalo, cobrado, monto_delta, motivo, created_at, autor:profiles!entrega_kit_cambios_created_by_fkey(nombre)')
    .eq('entrega_id', entregaId)
    .order('created_at', { ascending: false })
  if (error) { console.error('getHistorialKitEntrega:', error); return [] }
  return data || []
}
